import React, { useEffect, useState } from 'react';
import {
  Box,
  useMediaQuery,
  useTheme,
  Toolbar,
} from '@mui/material';
import { Outlet, useLocation } from 'react-router-dom';
import Navbar, { DRAWER_WIDTH, DRAWER_WIDTH_COLLAPSED } from './Navbar';
import { startSessionKeepAlive, stopSessionKeepAlive } from '../services/sessionKeepAlive';
import { EstabelecimentoProvider } from '../context/EstabelecimentoContext';
import useModulosLicenca from '../hooks/useModulosLicenca';
import ModuloBloqueado from './ModuloBloqueado';
import { getLicencaStatus } from '../services/api';
import { getLicencaBloqueio, setAuthSession, setLicencaBloqueio, setLicencaAviso } from '../services/authStorage';
import LicencaBloqueadaOverlay from './LicencaBloqueadaOverlay';
import LicencaAvisoBanner from './LicencaAvisoBanner';

const SIDEBAR_KEY = 'zen.sidebar.collapsed';

export default function Layout() {
  const theme = useTheme();
  const location = useLocation();
  const { rotaPermitida, moduloDaRota } = useModulosLicenca();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(SIDEBAR_KEY) === '1';
    } catch {
      return false;
    }
  });

  const [licencaBloqueio, setLicencaBloqueioState] = useState(() => getLicencaBloqueio());
  const [licencaAviso, setLicencaAvisoState] = useState({ mensagem: '', em: '' });

  useEffect(() => {
    startSessionKeepAlive();
    return () => stopSessionKeepAlive();
  }, []);

  useEffect(() => {
    const syncAviso = () => {
      setLicencaAvisoState({
        mensagem: localStorage.getItem('licencaAviso') || '',
        em: localStorage.getItem('licencaAvisoEm') || '',
      });
    };
    syncAviso();
    const onUpdate = () => {
      setLicencaBloqueioState(getLicencaBloqueio());
      syncAviso();
    };
    window.addEventListener('zen:licenca-atualizada', onUpdate);
    return () => window.removeEventListener('zen:licenca-atualizada', onUpdate);
  }, []);

  const aplicarStatusLicenca = async () => {
    const { data } = await getLicencaStatus();
    if (!data) return;
    setAuthSession({
      modulos: data.modulos || [],
      modulosAtivos: data.modulosAtivos || [],
      empresasPermitidas: data.empresasPermitidas || 1,
    });
    if (data.ativo === false) {
      setLicencaBloqueio(
        data.mensagemBloqueio ||
          'Licença bloqueada. Entre em contato com o suporte.'
      );
      setLicencaAviso('');
    } else {
      setLicencaBloqueio('');
      setLicencaAviso(data.avisoBloqueio || '', data.bloqueioProgramadoEm || '');
    }
  };

  useEffect(() => {
    let ativo = true;
    const syncLicenca = async () => {
      try {
        await aplicarStatusLicenca();
      } catch {
        /* offline / sem token — fail-open */
      }
      if (!ativo) return;
    };
    syncLicenca();
    const timer = window.setInterval(syncLicenca, 60 * 1000);
    return () => {
      ativo = false;
      clearInterval(timer);
    };
  }, []);

  const toggleCollapsed = () => {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(SIDEBAR_KEY, next ? '1' : '0');
      } catch {
        /* ignore */
      }
      return next;
    });
  };

  const handleDrawerToggle = () => {
    if (isDesktop) {
      toggleCollapsed();
      return;
    }
    setMobileOpen((open) => !open);
  };

  const currentWidth = isDesktop
    ? collapsed
      ? DRAWER_WIDTH_COLLAPSED
      : DRAWER_WIDTH
    : 0;

  const bloqueadoPorModulo = !rotaPermitida(location.pathname);

  return (
    <EstabelecimentoProvider>
    <Box sx={{ display: 'flex', overflowX: 'hidden' }}>
      <Navbar
        onMenuClick={handleDrawerToggle}
        isDesktop={isDesktop}
        drawerOpen={mobileOpen}
        setDrawerOpen={setMobileOpen}
        collapsed={collapsed}
        onToggleCollapse={toggleCollapsed}
      />
      <Box
        component="main"
        sx={{
          position: 'relative',
          flexGrow: 1,
          width: isDesktop ? `calc(100vw - ${currentWidth}px)` : '100vw',
          ml: 0,
          transition: (t) =>
            t.transitions.create('width', {
              easing: t.transitions.easing.sharp,
              duration: t.transitions.duration.enteringScreen,
            }),
          backgroundColor: 'background.default',
          minHeight: '100vh',
          px: { xs: 0, md: 2 },
          py: { xs: 1, md: 3 },
          overflowX: 'hidden',
          '&::after': {
            content: '""',
            position: 'fixed',
            right: { xs: -60, md: -80 },
            bottom: { xs: -60, md: -80 },
            width: { xs: 260, md: 420 },
            height: { xs: 260, md: 420 },
            backgroundImage: `url(${theme.zen.watermark})`,
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'contain',
            opacity: theme.zen.watermarkOpacity,
            pointerEvents: 'none',
            zIndex: 0,
          },
        }}
      >
        <Toolbar />
        {!licencaBloqueio && licencaAviso.mensagem ? (
          <LicencaAvisoBanner
            mensagem={licencaAviso.mensagem}
            bloqueioProgramadoEm={licencaAviso.em}
          />
        ) : null}
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          {bloqueadoPorModulo ? (
            <ModuloBloqueado modulo={moduloDaRota(location.pathname)} />
          ) : (
            <Outlet />
          )}
        </Box>
      </Box>
    </Box>
    {licencaBloqueio ? (
      <LicencaBloqueadaOverlay
        mensagem={licencaBloqueio}
        onVerificar={aplicarStatusLicenca}
      />
    ) : null}
    </EstabelecimentoProvider>
  );
}
